/* eslint-disable no-nested-ternary */
import { IPaginationResult } from '@models/Equipment.model';

import User, { IUser, IUserApiModel } from './User.model';

import Update, { IUpdate, IUpdateApiModel } from './Update.model';
import Tag, { ITag, ITagApiModel } from './Tag.model';

export const updateTypeOptions = <const>[
  'critical',
  'high',
  'low',
  'scheduled',
];
export type UpdateTypeOptions = typeof updateTypeOptions[number];

export const getTypeColor = (importance: UpdateTypeOptions) => {
  if (importance === 'critical') return '#EA1212';
  if (importance === 'high') return '#DF6F00';
  if (importance === 'low') return '#838A91';
  if (importance === 'scheduled') return '#249D0E';

  return '#249D0E';
};

export enum UpdateStatusType {
  NEW = 'new',
  PRIORITIZED = 'prioritized',
  COMPLETED = 'completed',
  DISMISSED = 'dismissed',
}

export enum UpdateStatusPriority {
  URGENT = 'urgent',
  IMPORTANT = 'important',
  NONCRITICAL = 'non-critial',
  UNCERTAIN = 'uncertain',
}

export interface IUpdateStatus {
  id: string;
  type: UpdateStatusType;
  priority: UpdateStatusPriority;
  assignee?: IUser;
  update: IUpdate;
  auth0Picture?: string;
  auth0Name?: string;
  productsEquipmentCount?: number;
  tags?: ITag[];
  dueDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUpdateStatusApiModel {
  id: string;
  type: UpdateStatusType;
  priority: UpdateStatusPriority;
  assignee?: IUserApiModel;
  auth0Name?: string;
  update: IUpdateApiModel;
  auth0Picture?: string;
  productsEquipmentCount?: number;
  tags?: ITagApiModel[];
  dueDate: string;
  createdAt: string;
  updatedAt: string;
}

export default class UpdateStatus implements IUpdateStatus {
  public readonly id: string;

  public readonly type: UpdateStatusType;

  public readonly priority: UpdateStatusPriority;

  public readonly assignee?: IUser;

  public readonly update: IUpdate;

  public readonly auth0Picture?: string;

  public readonly auth0Name?: string;

  public readonly productsEquipmentCount?: number;

  public readonly tags?: ITag[];

  public readonly dueDate: Date;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  constructor(
    id: string,
    type: UpdateStatusType,
    priority: UpdateStatusPriority,
    update: IUpdate,
    dueDate: Date,
    createdAt: Date,
    updatedAt: Date,
    assignee?: IUser,
    auth0Picture?: string,
    auth0Name?: string,
    productsEquipmentCount?: number,
    tags?: ITag[],
  ) {
    this.id = id;
    this.type = type;
    this.priority = priority;
    this.assignee = assignee;
    this.update = update;
    this.dueDate = dueDate;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.auth0Picture = auth0Picture;
    this.auth0Name = auth0Name;
    this.productsEquipmentCount = productsEquipmentCount;
    this.tags = tags;
  }

  static fromApiModel(apiModel: IUpdateStatusApiModel) {
    const {
      id,
      type,
      priority,
      auth0Picture,
      auth0Name,
      productsEquipmentCount,
    } = apiModel;

    const dueDate = new Date(Date.parse(apiModel.dueDate));
    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));

    const assignee = apiModel.assignee
      ? User.fromApiModel(apiModel.assignee)
      : undefined;
    const update = Update.fromApiModel(apiModel.update);
    const tags = apiModel.tags
      ? Tag.arrayFromApiModel(apiModel.tags)
      : undefined;

    return new UpdateStatus(
      id,
      type,
      priority,
      update,
      dueDate,
      createdAt,
      updatedAt,
      assignee,
      auth0Picture,
      auth0Name,
      productsEquipmentCount,
      tags,
    );
  }

  static arrayFromApiModel(arrayApiModel: IUpdateStatusApiModel[]) {
    const array = arrayApiModel.map(apiModel =>
      UpdateStatus.fromApiModel(apiModel),
    );

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<IUpdateStatusApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      UpdateStatus.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static arrayPaginatedFromApiAdmModel(
    arrayApiModel: IPaginationResult<IUpdateStatusApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      UpdateStatus.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }
}
